import { shallowRef } from 'vue'
import { DEFAULT_LOCALE } from '../plugins/i18n'

/**
 * The locale currently displayed, readable from plain functions.
 *
 * Formatting helpers are pure functions — `formatPrice`, and anything else that
 * hands a locale to `Intl` — so they cannot call `useI18n()`, which only works
 * inside a component's setup. Before this existed they simply hard-coded
 * `'fr-FR'`, which is how « Publishing a channel is a 10,00 € per month
 * subscription » ended up on screen: the sentence translated, the number did
 * not.
 *
 * It is a `ref`, and it has to be. A plain module variable looked tidier, but
 * `formatPrice` is called from inside `computed()`s whose other dependencies do
 * not move with the language — a channel's fee is the same figure in French and
 * in English. Reading a non-reactive value there means the computed never
 * invalidates, so switching language left « Publishing a channel is a
 * **10,00 €** per month subscription » on screen until something else happened
 * to re-render. Reading a ref makes every one of those computeds depend on the
 * locale, which is exactly what they should have depended on all along.
 */
const activeLocale = shallowRef<string | null>(null)

/**
 * Record the locale the app is displaying.
 *
 * Called by the i18n plugin at creation and by the locale store on every
 * change, so the two can never drift.
 *
 * @param code - A supported locale code, e.g. `fr` or `en`
 */
export function setActiveLocale(code: string): void {
  if (code) activeLocale.value = code
}

/**
 * The locale to hand to `Intl`.
 *
 * The codes are bare language tags (`fr`, `en`), which is valid BCP 47 — `Intl`
 * resolves them to the language's own conventions, so `fr` gives `10,00 €` and
 * `en` gives `€10.00`. No region table to maintain, and nothing to update when
 * a language is added to `SUPPORTED_LOCALES`.
 *
 * `DEFAULT_LOCALE` is read here rather than at module scope, and that placement
 * is load-bearing: `i18n.ts` imports this module to seed it, so the two form an
 * import cycle. Touching the imported constant while the modules are still
 * initialising would read it before its own module has run; inside a function
 * called at render time, the cycle has long since resolved.
 *
 * @returns The active locale code
 *
 * @example
 * new Intl.NumberFormat(getActiveLocale(), { style: 'currency', currency: 'EUR' })
 */
export function getActiveLocale(): string {
  return activeLocale.value ?? DEFAULT_LOCALE
}
